import { addDays, subDays } from 'date-fns'
import { when } from 'mobx'

import { isInView } from '@avantstay/backoffice-core'

import { DATE_BORDER_WIDTH, TAPE_CHART_HEADER_ROW_HEIGHT, TIME_FRAME_DAYS } from './config/contants'
import { storeHolder } from './internal'
import { tapeChartGridSizingStore } from './stores/TapeChartGridSizingStore'

enum ScrollDirection {
  Previous = 'previous',
  Next = 'next',
}

class ScrollController {
  private _container: HTMLDivElement | undefined = undefined

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private timeoutId: any

  private desiredScrollLeftPosition = 0

  setContainer(container: HTMLDivElement | null) {
    if (container && this._container !== container) {
      this._container = container
    }
  }

  addScrollListener(callback: VoidFunction) {
    if (this._container) {
      this._container.addEventListener('scroll', callback)
    }
  }

  removeScrollListener(callback: VoidFunction) {
    if (this._container) {
      this._container.removeEventListener('scroll', callback)
    }
  }

  get container() {
    return this._container
  }

  scrollTo(options: ScrollToOptions) {
    if (!this._container) {
      return
    }

    this._container.scrollTo(options)
  }

  scrollBy(options: ScrollToOptions) {
    if (!this._container) {
      return
    }
    this._container.scrollBy(options)
  }

  private isSwitchingViewFromNeeded = (direction: ScrollDirection) => {
    const offset = tapeChartGridSizingStore.dayColumnWidth * 7
    if (!this._container) {
      return undefined
    }
    if (direction === ScrollDirection.Previous) {
      return this._container.scrollLeft <= offset
    }
    return (
      this._container.scrollWidth - this._container.scrollLeft - this._container.clientWidth <=
      offset
    )
  }

  private getWeekScrollValue(direction: ScrollDirection) {
    if (!this._container) {
      return 0
    }

    const { extendedColumnsWidth } = storeHolder.store.extendedColumnsStore

    const scrollRest = this._container.scrollLeft % tapeChartGridSizingStore.dayColumnWidth

    if (direction === ScrollDirection.Previous) {
      return -tapeChartGridSizingStore.dayColumnWidth * 7 - scrollRest
    }

    const containerRest =
      (this._container.clientWidth -
        tapeChartGridSizingStore.firstColumnWidth -
        (extendedColumnsWidth ?? 0)) %
      tapeChartGridSizingStore.dayColumnWidth

    return (
      tapeChartGridSizingStore.dayColumnWidth * 7 - containerRest - scrollRest + DATE_BORDER_WIDTH
    )
  }

  private loadPreviousRange() {
    const rootStore = storeHolder.store
    const { setViewFrom, startDate } = rootStore.filterStore
    rootStore.apiStore.triggerLoadingTapesOverlay?.(true)

    setViewFrom?.(subDays(startDate!, TIME_FRAME_DAYS / 2))

    when(
      () => !rootStore.apiStore.isLoading,
      () => {
        this.scrollTo({
          left: (this.getDistance()?.left ?? 0) + this.getWeekScrollValue(ScrollDirection.Previous),
          behavior: 'smooth',
        })
      },
    )
  }

  private loadNextRange() {
    const rootStore = storeHolder.store
    const { setViewFrom, startDate } = rootStore.filterStore
    rootStore.apiStore.triggerLoadingTapesOverlay?.(true)

    setViewFrom?.(addDays(startDate!, TIME_FRAME_DAYS / 2))

    when(
      () => !rootStore.apiStore.isLoading,
      () => {
        this.scrollTo({
          left: (this.getDistance()?.left ?? 0) + this.getWeekScrollValue(ScrollDirection.Next),
          behavior: 'smooth',
        })
      },
    )
  }

  scrollToPreviousWeek() {
    if (!this._container) {
      return
    }

    if (this.isSwitchingViewFromNeeded(ScrollDirection.Previous)) {
      this.desiredScrollLeftPosition =
        (TIME_FRAME_DAYS / 2) * tapeChartGridSizingStore.dayColumnWidth +
        (this.getDistance()?.left || 0)

      this.loadPreviousRange()
    } else {
      this.scrollBy({
        left: this.getWeekScrollValue(ScrollDirection.Previous),
        behavior: 'smooth',
      })
    }
  }

  scrollToNextWeek() {
    if (!this._container) {
      return
    }

    if (this.isSwitchingViewFromNeeded(ScrollDirection.Next)) {
      this.desiredScrollLeftPosition =
        this._container.scrollLeft - (TIME_FRAME_DAYS / 2) * tapeChartGridSizingStore.dayColumnWidth

      this.loadNextRange()
    } else {
      this._container.scrollBy({
        left: this.getWeekScrollValue(ScrollDirection.Next),
        behavior: 'smooth',
      })
    }
  }

  scrollToDesiredLeftPosition() {
    this.scrollTo({ left: this.desiredScrollLeftPosition })

    if (this.desiredScrollLeftPosition !== 0) {
      const rootStore = storeHolder.store
      rootStore.apiStore.triggerLoadingTapesOverlay?.(false)

      this.desiredScrollLeftPosition = 0
    }
  }

  scrollIntoView(target?: EventTarget | HTMLDivElement | null, options?: ScrollIntoViewOptions) {
    if (target instanceof HTMLElement) {
      clearTimeout(this.timeoutId)
      this.timeoutId = setTimeout(
        () =>
          !isInView(target, true, this._container) &&
          target.scrollIntoView({
            behavior: options?.behavior ?? 'smooth',
            block: options?.block ?? 'center',
            inline: options?.inline ?? 'center',
          }),
        1000,
      )
    }
  }

  scrollToToday(target?: EventTarget | HTMLDivElement | null, options?: ScrollIntoViewOptions) {
    if (target instanceof HTMLElement) {
      target.scrollIntoView({
        behavior: options?.behavior ?? 'smooth',
        block: options?.block ?? 'center',
        inline: options?.inline ?? 'center',
      })
    }
  }

  getContainerAsync(): Promise<HTMLDivElement> {
    return new Promise(resolve => {
      const checkForContainer = () => {
        if (this._container) {
          resolve(this._container)
        } else {
          setTimeout(checkForContainer, 200)
        }
      }

      checkForContainer()
    })
  }

  async scrollToRow(rowId: number) {
    const container = await this.getContainerAsync()
    container.scrollTo({
      top:
        rowId * tapeChartGridSizingStore.propertyRowHeight -
        container.clientHeight / 2 +
        TAPE_CHART_HEADER_ROW_HEIGHT * 2,
      behavior: 'smooth',
    })
  }

  getDistance() {
    if (this._container) {
      return {
        left: this._container.scrollLeft,
        right:
          this._container.scrollWidth - this._container.scrollLeft - this._container.clientWidth,
      }
    }
    return undefined
  }
}

export const scrollController = new ScrollController()
